import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import config from '../config';

type Tab = 'signIn' | 'signUp' | 'confirm' | 'newPassword' | 'forgotPassword' | 'resetPassword';

const AuthPage = () => {
  const navigate = useNavigate();
  const { signIn, signUp, confirmSignUp, confirmNewPassword, resetPassword, confirmResetPassword } = useAuth();

  const [tab, setTab] = useState<Tab>('signIn');
  const allowSignup = config.allowSignup;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const result = await signIn(email, password);
      if (result.newPasswordRequired) {
        setTab('newPassword');
      } else {
        navigate('/app');
      }
    } catch (err: any) {
      setError(err.message ?? 'Sign in failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      await signUp(email, password);
      setTab('confirm');
    } catch (err: any) {
      setError(err.message ?? 'Sign up failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await confirmSignUp(email, code);
      await signIn(email, password);
      navigate('/app');
    } catch (err: any) {
      setError(err.message ?? 'Confirmation failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await resetPassword(email);
      setTab('resetPassword');
    } catch (err: any) {
      setError(err.message ?? 'Failed to send reset code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== newPasswordConfirm) {
      setError('Passwords do not match');
      return;
    }
    setIsLoading(true);
    try {
      await confirmResetPassword(email, resetCode, newPassword);
      setTab('signIn');
      setError('');
    } catch (err: any) {
      setError(err.message ?? 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-parchment-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center gap-2">
            <span className="text-4xl">📖</span>
            <span className="text-3xl font-bold text-ink">TrackBible</span>
          </a>
        </div>

        <div className="bg-parchment-100 rounded-2xl shadow-lg border border-parchment-200 p-8">
          {/* Tabs — hidden when confirming */}
          {tab !== 'confirm' && allowSignup && (
            <div className="flex rounded-lg bg-parchment-200 p-1 mb-6">
              <button
                onClick={() => { setTab('signIn'); setError(''); }}
                className={`flex-1 py-2 rounded-md text-sm font-semibold transition ${
                  tab === 'signIn' ? 'bg-parchment-50 shadow text-ink' : 'text-ink-muted hover:text-ink'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => { setTab('signUp'); setError(''); }}
                className={`flex-1 py-2 rounded-md text-sm font-semibold transition ${
                  tab === 'signUp' ? 'bg-parchment-50 shadow text-ink' : 'text-ink-muted hover:text-ink'
                }`}
              >
                Create Account
              </button>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Sign In Form */}
          {tab === 'signIn' && (
            <form onSubmit={handleSignIn} className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Email</label>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border border-parchment-300 rounded-lg px-3 py-2 text-sm bg-parchment-50 focus:outline-none focus:ring-2 focus:ring-leather"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Password</label>
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full border border-parchment-300 rounded-lg px-3 py-2 text-sm bg-parchment-50 focus:outline-none focus:ring-2 focus:ring-leather"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-leather hover:bg-leather-light text-parchment-100 font-semibold rounded-lg transition disabled:opacity-50"
              >
                {isLoading ? 'Signing in…' : 'Sign In'}
              </button>
              <button
                type="button"
                onClick={() => { setTab('forgotPassword'); setError(''); }}
                className="text-sm text-ink-muted hover:text-ink text-center w-full mt-1"
              >
                Forgot password?
              </button>
            </form>
          )}

          {/* Sign Up Form */}
          {tab === 'signUp' && (
            <form onSubmit={handleSignUp} className="flex flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Email</label>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border border-parchment-300 rounded-lg px-3 py-2 text-sm bg-parchment-50 focus:outline-none focus:ring-2 focus:ring-leather"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Password</label>
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full border border-parchment-300 rounded-lg px-3 py-2 text-sm bg-parchment-50 focus:outline-none focus:ring-2 focus:ring-leather"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Confirm Password</label>
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full border border-parchment-300 rounded-lg px-3 py-2 text-sm bg-parchment-50 focus:outline-none focus:ring-2 focus:ring-leather"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-leather hover:bg-leather-light text-parchment-100 font-semibold rounded-lg transition disabled:opacity-50"
              >
                {isLoading ? 'Creating account…' : 'Create Account'}
              </button>
            </form>
          )}

          {/* Confirm Code Form */}
          {tab === 'confirm' && (
            <form onSubmit={handleConfirm} className="flex flex-col gap-4">
              <div className="text-center mb-2">
                <p className="text-ink font-medium">Check your email</p>
                <p className="text-sm text-ink-muted mt-1">
                  We sent a verification code to <strong>{email}</strong>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Verification Code</label>
                <input
                  type="text"
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  className="w-full border border-parchment-300 rounded-lg px-3 py-2 text-sm text-center tracking-widest bg-parchment-50 focus:outline-none focus:ring-2 focus:ring-leather"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-leather hover:bg-leather-light text-parchment-100 font-semibold rounded-lg transition disabled:opacity-50"
              >
                {isLoading ? 'Verifying…' : 'Verify & Sign In'}
              </button>
              <button
                type="button"
                onClick={() => { setTab('signUp'); setError(''); }}
                className="text-sm text-ink-muted hover:text-ink text-center"
              >
                ← Back to sign up
              </button>
            </form>
          )}

          {/* New Password Required Form (admin-created accounts) */}
          {tab === 'newPassword' && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setError('');
                if (newPassword !== newPasswordConfirm) {
                  setError('Passwords do not match');
                  return;
                }
                setIsLoading(true);
                try {
                  await confirmNewPassword(newPassword);
                  navigate('/app');
                } catch (err: any) {
                  setError(err.message ?? 'Failed to set password');
                } finally {
                  setIsLoading(false);
                }
              }}
              className="flex flex-col gap-4"
            >
              <div className="text-center mb-2">
                <p className="text-ink font-medium">Set your password</p>
                <p className="text-sm text-ink-muted mt-1">
                  Please choose a permanent password to continue.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1">New Password</label>
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full border border-parchment-300 rounded-lg px-3 py-2 text-sm bg-parchment-50 focus:outline-none focus:ring-2 focus:ring-leather"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Confirm Password</label>
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={newPasswordConfirm}
                  onChange={(e) => setNewPasswordConfirm(e.target.value)}
                  placeholder="••••••••"
                  className="w-full border border-parchment-300 rounded-lg px-3 py-2 text-sm bg-parchment-50 focus:outline-none focus:ring-2 focus:ring-leather"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-leather hover:bg-leather-light text-parchment-100 font-semibold rounded-lg transition disabled:opacity-50"
              >
                {isLoading ? 'Saving…' : 'Set Password & Sign In'}
              </button>
            </form>
          )}
          {/* Forgot Password Form */}
          {tab === 'forgotPassword' && (
            <form onSubmit={handleForgotPassword} className="flex flex-col gap-4">
              <div className="text-center mb-2">
                <p className="text-ink font-medium">Reset your password</p>
                <p className="text-sm text-ink-muted mt-1">
                  Enter your email and we'll send you a reset code.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Email</label>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full border border-parchment-300 rounded-lg px-3 py-2 text-sm bg-parchment-50 focus:outline-none focus:ring-2 focus:ring-leather"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-leather hover:bg-leather-light text-parchment-100 font-semibold rounded-lg transition disabled:opacity-50"
              >
                {isLoading ? 'Sending…' : 'Send Reset Code'}
              </button>
              <button
                type="button"
                onClick={() => { setTab('signIn'); setError(''); }}
                className="text-sm text-ink-muted hover:text-ink text-center"
              >
                ← Back to sign in
              </button>
            </form>
          )}

          {/* Reset Password Form */}
          {tab === 'resetPassword' && (
            <form onSubmit={handleConfirmReset} className="flex flex-col gap-4">
              <div className="text-center mb-2">
                <p className="text-ink font-medium">Enter your new password</p>
                <p className="text-sm text-ink-muted mt-1">
                  We sent a reset code to <strong>{email}</strong>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Reset Code</label>
                <input
                  type="text"
                  required
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  placeholder="123456"
                  className="w-full border border-parchment-300 rounded-lg px-3 py-2 text-sm text-center tracking-widest bg-parchment-50 focus:outline-none focus:ring-2 focus:ring-leather"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1">New Password</label>
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="w-full border border-parchment-300 rounded-lg px-3 py-2 text-sm bg-parchment-50 focus:outline-none focus:ring-2 focus:ring-leather"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink mb-1">Confirm Password</label>
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={newPasswordConfirm}
                  onChange={(e) => setNewPasswordConfirm(e.target.value)}
                  placeholder="••••••••"
                  className="w-full border border-parchment-300 rounded-lg px-3 py-2 text-sm bg-parchment-50 focus:outline-none focus:ring-2 focus:ring-leather"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-leather hover:bg-leather-light text-parchment-100 font-semibold rounded-lg transition disabled:opacity-50"
              >
                {isLoading ? 'Saving…' : 'Set New Password'}
              </button>
              <button
                type="button"
                onClick={() => { setTab('forgotPassword'); setError(''); }}
                className="text-sm text-ink-muted hover:text-ink text-center"
              >
                ← Resend code
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
